import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { FeedItem } from "@scrile/contracts";
import { ApiError, describeError, type ApiErrorCode } from "../api/errors";
import type { HeroApi } from "../api/types";
import { cacheKey, cacheMatches, clearFeedCache, FEED_CACHE_TTL_MS, loadFeedCache, saveFeedCache } from "./cache";
import { createChatFeed } from "./chatFeed";
import { throttleMediaRefresh } from "./mediaRefresh";
import { FEED_PAGE_SIZE, isHeroMessage, lastSeq, mergeFeed, type FeedPage } from "./feedItems";
import { abortableSleep, FeedPoller } from "./poller";
import { extensionFor, type VoiceRecording } from "./recorder";
import {
  discard as discardOutgoing,
  dueRetries,
  enqueue,
  markAttempting,
  markFailed,
  markSent,
  markUploaded,
  nextRetryAt,
  reconcile,
  requestBody,
  retryNow,
  type Outgoing,
} from "./sendQueue";
import { typingState, type TypingKind } from "./typing";

/**
 * Состояние «Мессенджера»: лента (long-poll), исходящие, «печатает», пейвол по 402.
 *
 * Жизнь цикла ленты привязана к видимости вкладки: скрыта — запрос обрывается и новый не
 * идёт (сервер держит не больше трёх ожиданий на подписчика, телефон в кармане не должен
 * занимать ни одного); вернулась, появилась сеть — сразу спрашиваем, без паузы.
 */

export type MessengerFatal = "unauthorized" | "not_found";

export interface ArrivedMeta {
  /** Первая страница после открытия — история, а не новые сообщения: её не объявляем. */
  history: boolean;
}

export interface MessengerOptions {
  api: HeroApi;
  userId: string;
  heroSlug: string;
  /** Пришло новое от ленты (для объявления и счётчика «new messages»). */
  onArrived?: (items: FeedItem[], meta: ArrivedMeta) => void;
  /** 402 на отправке — доступа нет (подписка кончилась, пилот позади). */
  onPaymentRequired?: () => void;
  now?: () => number;
}

export interface Messenger {
  items: FeedItem[];
  outgoing: Outgoing[];
  /** Лента показана: пришла первая страница или кеш вкладки. */
  loaded: boolean;
  /** Сеть моргнула — цикл повторяет; показать тихое «Reconnecting…». */
  reconnecting: boolean;
  fatal: MessengerFatal | null;
  typing: TypingKind | null;
  /** 402 на отправке: показать пейвол, даже если лента о нём не сказала. */
  paymentRequired: boolean;
  /** Короткое сообщение над полем ввода (429 и т. п.). */
  notice: { text: "slow_down"; at: number } | null;
  sendText(text: string): void;
  sendVoice(recording: VoiceRecording): void;
  retry(id: string): void;
  discard(id: string): void;
  clearPaymentRequired(): void;
  startError: string | null;
  retryStart(): void;
  refreshMedia(): void;
}

function newLocalId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // Небезопасный контекст (http по адресу в сети): randomUUID нет
  }
  return `local-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function errorCode(error: unknown): ApiErrorCode {
  return error instanceof ApiError ? error.code : "network";
}

interface Hint {
  nextAt: number;
  nextKind: FeedPage["nextKind"];
  hintAt: number;
}

export function useMessenger(options: MessengerOptions): Messenger {
  const { api, userId, heroSlug } = options;
  const now = options.now ?? Date.now;
  const key = cacheKey(userId, heroSlug);

  // Кеш вкладки — сразу в первый рендер: после обновления переписка на месте без мигания
  const initial = useMemo(() => loadFeedCache(key), [key]);
  const [items, setItems] = useState<FeedItem[]>(() => initial?.items ?? []);
  const [loaded, setLoaded] = useState(() => initial !== null);
  const [outgoing, setOutgoingState] = useState<Outgoing[]>([]);
  const [reconnecting, setReconnecting] = useState(false);
  const [fatal, setFatal] = useState<MessengerFatal | null>(null);
  const [hint, setHint] = useState<Hint | null>(null);
  const [awaitingSince, setAwaitingSince] = useState<number | null>(null);
  const [typing, setTyping] = useState<TypingKind | null>(null);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [notice, setNotice] = useState<Messenger["notice"]>(null);
  const [startError, setStartError] = useState<string | null>(null);

  const itemsRef = useRef(items);
  const outgoingRef = useRef(outgoing);
  const pollerRef = useRef<FeedPoller | null>(null);
  const chatFeedRef = useRef<ReturnType<typeof createChatFeed> | null>(null);
  const inFlight = useRef(new Set<string>());
  const callbacks = useRef(options);
  callbacks.current = options;
  const requestMediaRefresh = useMemo(() => throttleMediaRefresh(() => pollerRef.current?.refreshHistory(), now), [now]);
  const refreshMedia = useCallback(() => {
    if (fatal || document.visibilityState === "hidden") return;
    requestMediaRefresh();
  }, [fatal, requestMediaRefresh]);

  const setOutgoing = useCallback((update: (queue: Outgoing[]) => Outgoing[]) => {
    outgoingRef.current = update(outgoingRef.current);
    setOutgoingState(outgoingRef.current);
  }, []);

  // --- лента -----------------------------------------------------------------------

  useEffect(() => {
    const cached = loadFeedCache(key);
    let validated = cached === null;
    let first = true;
    let ended = false;
    let cacheSavedAt = cached?.savedAt ?? now();
    let refreshing = false;
    const chatFeed = createChatFeed(api, {
      onStartError: (error) => setStartError(error === null ? null : describeError(error)),
      onPaymentRequired: () => {
        setPaymentRequired(true);
        callbacks.current.onPaymentRequired?.();
      },
    });
    chatFeedRef.current = chatFeed;
    const poller = new FeedPoller(
      {
        fetchPage: (input) => chatFeed.fetch(input),
        now,
        sleep: abortableSleep,
        onRefresh: () => { validated = true; first = true; refreshing = true; },
        onPage: (page, meta) => {
          let fresh = page.items;
          if (!validated && cached) {
            validated = true;
            if (!cacheMatches(cached, page.items)) {
              // Кеш не сошёлся с сервером — переписка там другая: с нуля
              clearFeedCache(key);
              itemsRef.current = [];
              setItems([]);
              setLoaded(false);
              poller.resetCursor(0);
              return;
            }
          }
          setReconnecting(false);
          if (refreshing && page.count < FEED_PAGE_SIZE) {
            cacheSavedAt = meta.receivedAt;
            refreshing = false;
          }
          setHint(page.nextInMs !== null ? { nextAt: meta.receivedAt + page.nextInMs, nextKind: page.nextKind, hintAt: meta.receivedAt } : null);
          const before = itemsRef.current;
          const merged = mergeFeed(before, fresh);
          const known = new Set(before.map((item) => item.seq));
          fresh = fresh.filter((item) => !known.has(item.seq));
          if (merged !== before) {
            itemsRef.current = merged;
            setItems(merged);
            saveFeedCache(key, { lastSeq: Math.max(poller.cursor, lastSeq(merged)), items: merged, savedAt: cacheSavedAt });
          } else if (page.maxSeq !== null) {
            saveFeedCache(key, { lastSeq: Math.max(poller.cursor, lastSeq(merged)), items: merged, savedAt: cacheSavedAt });
          }
          if (fresh.some(isHeroMessage)) setAwaitingSince(null);
          if (outgoingRef.current.length > 0) {
            const { queue } = reconcile(outgoingRef.current, merged);
            if (queue.length !== outgoingRef.current.length) {
              // Зеркала пришли — местные пузыри больше не нужны; свой адрес записи освобождаем
              for (const gone of outgoingRef.current.filter((o) => !queue.includes(o))) {
                if (gone.body.kind === "voice" && gone.body.localUrl) URL.revokeObjectURL(gone.body.localUrl);
              }
              setOutgoing(() => queue);
            }
          }
          setLoaded(true);
          if (fresh.length > 0) callbacks.current.onArrived?.(fresh, { history: first });
          if (page.count < FEED_PAGE_SIZE) first = false;
        },
        onError: () => setReconnecting(true),
        onFatal: (error) => {
          ended = true;
          setHint(null);
          setAwaitingSince(null);
          setLoaded(true);
          setFatal(error instanceof ApiError && error.code === "not_found" ? "not_found" : "unauthorized");
        },
      },
      // Проверка кеша: элемент с последним seq должен прийти снова — спрашиваем с seq − 1
      { cursor: cached ? Math.max(0, cached.lastSeq - 1) : 0, refreshEveryMs: FEED_CACHE_TTL_MS },
    );
    pollerRef.current = poller;

    const visible = () => !ended && (typeof document === "undefined" || document.visibilityState !== "hidden");
    if (visible()) poller.start();
    const onVisibility = () => (visible() ? poller.start() : poller.stop());
    const onOnline = () => {
      if (visible()) poller.restart();
    };
    const onPageHide = () => poller.stop();
    const onPageShow = () => {
      if (visible()) poller.start();
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      poller.stop();
      pollerRef.current = null;
      chatFeedRef.current = null;
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("pageshow", onPageShow);
    };
    // now — стабильная функция; перезапуск цикла только при смене API, человека или героя
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, key, setOutgoing]);

  // --- «печатает» --------------------------------------------------------------------

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const evaluate = () => {
      const state = typingState({
        now: now(),
        nextAt: hint?.nextAt ?? null,
        nextKind: hint?.nextKind ?? null,
        hintAt: hint?.hintAt ?? null,
        awaitingSince,
      });
      setTyping(state.kind);
      if (state.recheckAt !== null) timer = setTimeout(evaluate, Math.max(16, state.recheckAt - now()));
    };
    evaluate();
    return () => {
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hint, awaitingSince]);

  // --- отправка ----------------------------------------------------------------------

  const attempt = useCallback(
    async (id: string) => {
      if (inFlight.current.has(id)) return;
      const current = outgoingRef.current.find((item) => item.id === id);
      if (!current) return;
      inFlight.current.add(id);
      setOutgoing((queue) => markAttempting(queue, id));
      try {
        let item = current;
        if (item.body.kind === "voice" && !item.body.voiceAssetKey) {
          const body = item.body;
          if (!body.blob) throw new ApiError("validation", 400, "Recording is gone");
          const uploaded = await api.uploadVoice({
            blob: body.blob,
            durationMs: body.durationMs,
            filename: `voice.${extensionFor(body.mime)}`,
          });
          setOutgoing((queue) => markUploaded(queue, id, uploaded.voiceAssetKey));
          item = outgoingRef.current.find((candidate) => candidate.id === id) ?? item;
        }
        const request = requestBody(item);
        if (!request) throw new ApiError("validation", 400, "Nothing to send");
        const accepted = await api.sendMessage(request, { idempotencyKey: id });
        setOutgoing((queue) => markSent(queue, id, accepted.seq));
        setAwaitingSince(now());
        // Зеркало могло прийти лентой раньше ответа на POST
        const { queue } = reconcile(outgoingRef.current, itemsRef.current);
        if (queue.length !== outgoingRef.current.length) {
          for (const gone of outgoingRef.current.filter((candidate) => !queue.includes(candidate))) {
            if (gone.body.kind === "voice" && gone.body.localUrl) URL.revokeObjectURL(gone.body.localUrl);
          }
          setOutgoing(() => queue);
        }
      } catch (error) {
        const code = errorCode(error);
        const retryAfterSec = error instanceof ApiError ? error.retryAfterSec : null;
        setOutgoing((queue) => markFailed(queue, id, { code, now: now(), retryAfterSec }));
        if (code === "payment_required") {
          setPaymentRequired(true);
          callbacks.current.onPaymentRequired?.();
        } else if (code === "rate_limited") {
          setNotice({ text: "slow_down", at: now() });
        } else if (code === "unauthorized") {
          setFatal("unauthorized");
        }
      } finally {
        inFlight.current.delete(id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [api, setOutgoing],
  );

  // Авто-повтор по сроку из очереди
  useEffect(() => {
    const at = nextRetryAt(outgoing);
    if (at === null) return;
    const timer = setTimeout(() => {
      for (const id of dueRetries(outgoingRef.current, now())) void attempt(id);
    }, Math.max(0, at - now()));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [outgoing, attempt]);

  // «Slow down a little» — несколько секунд, не навсегда
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(null), 4_000);
    return () => clearTimeout(timer);
  }, [notice]);

  const afterSeq = () => Math.max(pollerRef.current?.cursor ?? 0, lastSeq(itemsRef.current));

  const sendText = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      const id = newLocalId();
      setOutgoing((queue) => enqueue(queue, { id, body: { kind: "text", text: trimmed }, now: now(), afterSeq: afterSeq() }));
      void attempt(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attempt, setOutgoing],
  );

  const sendVoice = useCallback(
    (recording: VoiceRecording) => {
      const id = newLocalId();
      let localUrl: string | null = null;
      try {
        localUrl = URL.createObjectURL(recording.blob);
      } catch {
        localUrl = null;
      }
      setOutgoing((queue) =>
        enqueue(queue, {
          id,
          body: { kind: "voice", blob: recording.blob, localUrl, mime: recording.mime, durationMs: recording.durationMs, voiceAssetKey: null },
          now: now(),
          afterSeq: afterSeq(),
        }),
      );
      void attempt(id);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attempt, setOutgoing],
  );

  const retry = useCallback(
    (id: string) => {
      setOutgoing((queue) => retryNow(queue, id));
      void attempt(id);
    },
    [attempt, setOutgoing],
  );

  const discard = useCallback(
    (id: string) => {
      const item = outgoingRef.current.find((candidate) => candidate.id === id);
      if (item?.body.kind === "voice" && item.body.localUrl) URL.revokeObjectURL(item.body.localUrl);
      setOutgoing((queue) => discardOutgoing(queue, id));
    },
    [setOutgoing],
  );

  const clearPaymentRequired = useCallback(() => {
    setPaymentRequired(false);
    chatFeedRef.current?.retryStart();
  }, []);
  const retryStart = useCallback(() => {
    setStartError(null);
    chatFeedRef.current?.retryStart();
    pollerRef.current?.restart();
  }, []);

  // Уход с экрана — местные адреса записей больше не нужны
  useEffect(
    () => () => {
      for (const item of outgoingRef.current) {
        if (item.body.kind === "voice" && item.body.localUrl) URL.revokeObjectURL(item.body.localUrl);
      }
    },
    [],
  );

  return {
    items,
    outgoing,
    loaded,
    reconnecting,
    fatal,
    typing,
    paymentRequired,
    notice,
    sendText,
    sendVoice,
    retry,
    discard,
    clearPaymentRequired,
    startError,
    retryStart,
    refreshMedia,
  };
}
